import React, {
  useEffect,
  useRef,
  useState,
} from "react";

import {
  useCreateLinkedinRequestsMutation,
  useUpdateLinkedinRequestsMutation,
} from "presentation/hooks/use-query-linkedinrequests";

// Hooks de país/cidade via dropdown desativados: frontend passou a usar texto livre.
// Mantidos comentados para eventual reativação futura.
// import {
//   useParameterByFilter,
// } from "presentation/hooks/use-query-parameter";

import InputDateComponent from "presentation/components/Shared/InputDateComponent";
import InputTextComponent from "presentation/components/Shared/InputTextComponent";

import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { Dropdown } from "primereact/dropdown";
import { FloatLabel } from "primereact/floatlabel";
import { Tooltip } from "primereact/tooltip";

import { useAuthStore } from "presentation/stores/use-auth-store";
import { useLinkedinRequestsSelectedStore } from "presentation/stores/use-linkedinrequests-selected-store";

// import { BaseInputFilter } from "domain/models/base-input-filter";
import { LinkedinRequestsPayload } from "domain/entities/linkedinrequests";

import {
  jobTypeOptions,
  experienceLevelOptions,
  regimeTypeOptions,
  getOptionLabel,
  rangePostedOptions,
  formatRangePostedLabel,
} from "../linkedinrequests-options";
import { formatDateSafe } from "presentation/utils/date-formatter";
import InputNumberComponent from "presentation/components/Shared/InputNumberComponent";
import styles from "../../Shared/index.module.css";

type Props = {
  mode: "create" | "edit" | "view";
  initialData?: any;
  id?: string;
  onSuccess?: () => void;
};

type LinkedinRequestsFormType = {
  id: string;
  position: string;
  city: string;
  country: string;
  jobType?: string;
  experienceLevel?: string;
  regimeType?: string;
  rangePosted: string;
  isRecurring: boolean;
  daysRange: number;
  nextExecution: Date | null;
  fineshedExecution?: Date | null;
  status: string;
  createdBy: string;
  lastEmailedAt: Date | null;
  createdAt: Date | null;
  modifiedAt: Date | null;
};


const emptyForm: LinkedinRequestsFormType = {
  id: "",
  position: "",
  city: "",
  country: "",
  jobType: "",
  experienceLevel: "",
  regimeType: "",
  rangePosted: "",
  isRecurring: false,
  daysRange: 1,
  nextExecution: null,
  fineshedExecution: null,
  status: "",
  createdBy: "",
  lastEmailedAt: null,
  createdAt: null,
  modifiedAt: null,
};

const LinkedinRequestsForm: React.FC<Props> = ({
  mode,
  initialData,
  id,
  onSuccess,
}) => {
  const toast = useRef<Toast>(null);

  const { email: userEmployeeEmail } = useAuthStore();

  const { selectedRequest } = useLinkedinRequestsSelectedStore();

  const isViewMode = mode === "view";

  const effectiveInitialData =
    mode === "create" ? null : initialData || selectedRequest;

  const [form, setForm] = useState<LinkedinRequestsFormType>(emptyForm);
  // const [workLocationType, setWorkLocationType] = useState<string>("");

  const normalizeForm = (data: any = {}): LinkedinRequestsFormType => ({
    id: data.id ?? "",
    position: data.position ?? "",
    country: data.country ?? "",
    city: data.city ?? "",
    jobType: data.jobType ?? "",
    experienceLevel: data.experienceLevel ?? "",
    regimeType: data.regimeType ?? "",
    rangePosted: data.rangePosted ?? "",
    isRecurring: data.isRecurring ?? false,
    daysRange: data.daysRange ?? 1,
    nextExecution: data.nextExecution ? new Date(data.nextExecution) : null,
    fineshedExecution: data.fineshedExecution ? new Date(data.fineshedExecution) : null,
    status: data.status ?? "",
    createdBy: data.createdBy ?? "",
    lastEmailedAt: data.lastEmailedAt ? new Date(data.lastEmailedAt) : null,
    createdAt: data.createdAt ? new Date(data.createdAt) : null,
    modifiedAt: data.modifiedAt ? new Date(data.modifiedAt) : null,
  });

  useEffect(() => {
    if (mode === "create") {
      setForm(emptyForm);
      return;
    }

    if (effectiveInitialData) {
      setForm(normalizeForm(effectiveInitialData));
    }
  }, [effectiveInitialData, mode]);

  const handleChange = (
    field: keyof LinkedinRequestsFormType,
    value: any
  ) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  /* País/cidade agora são texto livre; hooks e efeito de dependência país->cidade abaixo
     desativados (comentados) porque deixaram de fazer sentido com dropdown removido, e o
     efeito de limpar "city" a cada mudança de "country" passaria a disparar a cada tecla
     digitada, apagando o campo enquanto o utilizador escreve. Mantido para reaplicação futura.

  const { data: countriesResponse } = useParameterByFilter({
    page: 1,
    limit: 500,
    type: "workcountry",
  } as BaseInputFilter);

  const countryOptions =
    countriesResponse?.data?.map((item: any) => ({
      label: item.value,
      value: item.value,
    })) || [];

  // Por enquanto passa a ser feito manualmente, escrevendo em inglês, mas como estes campos de parametrização podem mudar a todo tempo, vamos deixar como comentário,
  // para caso seja necessário voltar a fazer de forma automática, com base na parametrização.

  // useEffect(() => {
  //   if (form.country === "Portugal") {
  //     setWorkLocationType("worklocation-pt");
  //   } else if (form.country === "Canadá") {
  //     setWorkLocationType("worklocation-ca");
  //   } else if (form.country === "Espanha") {
  //     setWorkLocationType("worklocation-es");
  //   } else if (form.country === "Suíça") {
  //     setWorkLocationType("worklocation-sui");
  //   } else{
  //     setWorkLocationType("");
  //   }
  // }, [form.country]);

  const { data: citiesResponse } = useParameterByFilter(
    { page: 1, limit: 500, type: workLocationType } as BaseInputFilter,
    { enabled: !!workLocationType }
  );

  const cityOptions =
    citiesResponse?.data?.map((item: any) => ({
      label: item.value,
      value: item.value,
    })) || [];

  const previousCountry = useRef(form.country);

  useEffect(() => {
    if (previousCountry.current && previousCountry.current !== form.country) {
      setForm((prev) => ({ ...prev, city: "" }));
    }
    previousCountry.current = form.country;
  }, [form.country]);
  */

  const buildPayload = (): LinkedinRequestsPayload => ({
    id: form.id,
    position: form.position,
    country: form.country,
    city: form.city,
    jobType: form.jobType || undefined,
    experienceLevel: form.experienceLevel || undefined,
    regimeType: form.regimeType || undefined,
    rangePosted: form.rangePosted,
    isRecurring: form.isRecurring,
    daysRange: form.daysRange,
    nextExecution: form.nextExecution ?? new Date(),
    fineshedExecution: form.fineshedExecution ?? null,
    status: form.status,
    createdBy: userEmployeeEmail || "",
  });

  const createMutation = useCreateLinkedinRequestsMutation();
  const updateMutation = useUpdateLinkedinRequestsMutation();

  const handleSubmit = async () => {
    try {
      if (!form.position || !form.country || !form.rangePosted) {
        toast.current?.show({
          severity: "error",
          summary: "Erro",
          detail: "Preencha todos os campos obrigatórios.",
          life: 5000,
        });
        return;
      }

      const payload = buildPayload();

      if (mode === "create") {
        await createMutation.mutateAsync(payload);
      } else {
        if (!id) throw new Error("ID inválido");
        await updateMutation.mutateAsync({ ...payload, id });
      }

      toast.current?.show({
        severity: "success",
        summary: "Sucesso",
        detail:
          mode === "create"
            ? "Pedido criado com sucesso."
            : "Pedido atualizado com sucesso.",
        life: 3000,
      });

      onSuccess?.();
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Erro",
        detail: error?.message || "Erro ao guardar pedido.",
        life: 5000,
      });
    }
  };

  const minFineshedExecution = (() => {
    const base = form.nextExecution ? new Date(form.nextExecution) : new Date();
    base.setDate(base.getDate() + (form.daysRange || 0));
    return base;
  })();

  useEffect(() => {
    if (
      form.fineshedExecution &&
      new Date(form.fineshedExecution) < minFineshedExecution
    ) {
      setForm((prev) => ({ ...prev, fineshedExecution: null }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.daysRange, form.nextExecution]);

  const inputStyles: React.CSSProperties = {
      color: "#1e293b",
      opacity: 1,
      fontWeight: 500,
    }; 

  return (
    <div className="p-fluid mt-4" style={{ paddingTop: "30px" }}>
      <Toast ref={toast} position="bottom-right" />

      {/* Linha 1: Posição | País | Cidade | Intervalo de Publicação */}
      <div className="grid mb-4">
        <div className="col-3">
          <InputTextComponent
            label="Posição *"
            fieldName="position"
            currentObject={form}
            getValue={form.position}
            setValue={setForm}
            isDisable={isViewMode}
            style={inputStyles}
          />
        </div>

        <div className="col-3">
            <InputTextComponent
              label="País"
              fieldName="country"
              currentObject={form}
              getValue={form.country}
              setValue={setForm}
              isDisable={isViewMode}
              style={inputStyles}
            />
            {/* este dropdown foi desativado, 
              pois agora os dados são inseridos manualmente pelo utilizado.
              havia no backend, uam forma de passar a pesquisa de português para inglês, mas como os campos de parametrização podem mudar a todo tempo,
              vamos deixar por conta do uusuário, que vai escrever o país em inglês, para que a pesquisa funcione corretamente.

              Deixei o código comentado, caso seja necessário voltar a fazer de forma automática, com base na parametrização.
              Vê o commit refactor(Linkedin): Chenge dropdown country and city to input text, 09/07/2026, para ver como era a estrutura antiga.
            */}
            {/* 
            <FloatLabel>
              <Dropdown
                inputId="country"
                value={form.country}
                options={countryOptions}
                optionLabel="label"
                optionValue="value"
                className="w-full"
                filter
                showClear
                onChange={(e) => handleChange("country", e.value)}
              />
              <label className={styles.label} htmlFor="country">País *</label>
            </FloatLabel> */}
        </div>

        <div className="col-3">
            <InputTextComponent
              label="Cidade"
              fieldName="city"
              currentObject={form}
              getValue={form.city}
              setValue={setForm}
              isDisable={isViewMode}
              style={inputStyles}
            />
            {/* <FloatLabel>
              <Dropdown
                inputId="city"
                value={form.city}
                options={cityOptions}
                optionLabel="label"
                optionValue="value"
                className="w-full"
                filter
                showClear
                disabled={!form.country}
                onChange={(e) => handleChange("city", e.value)}
              />
              <label className={styles.label} htmlFor="city">Cidade *</label>
            </FloatLabel> */}
        </div>

        <div className="col-3">
          {isViewMode ? (
            <InputTextComponent
              label="Intervalo de Publicação"
              fieldName="rangePosted"
              currentObject={form}
              getValue={formatRangePostedLabel(form.rangePosted)}
              setValue={setForm}
              isDisable
              style={inputStyles}
            />
          ) : (
            <FloatLabel>
              <Dropdown
                inputId="rangePosted"
                value={form.rangePosted}
                options={rangePostedOptions}
                optionLabel="label"
                optionValue="value"
                className="w-full"
                showClear
                onChange={(e) => handleChange("rangePosted", e.value ?? "")}
              />
              <label className={styles.label} htmlFor="rangePosted">Intervalo de Publicação *</label>
            </FloatLabel>
          )}
        </div>
      </div>  
        

      {/* Linha 2: Tipo de trabalho | Regime | Experiência | Recorrente */}
      <div className="grid mb-4">

        <div className="col-3">
          {isViewMode ? (
            <InputTextComponent
              label="Tipo de trabalho"
              fieldName="jobType"
              currentObject={form}
              getValue={getOptionLabel(jobTypeOptions, form.jobType)}
              setValue={setForm}
              isDisable
              style={inputStyles}
            />
          ) : (
            <FloatLabel>
              <Dropdown
                inputId="jobType"
                value={form.jobType}
                options={jobTypeOptions}
                optionLabel="label"
                optionValue="value"
                className="w-full"
                filter
                showClear
                onChange={(e) => handleChange("jobType", e.value)}
              />
              <label className={styles.label} htmlFor="jobType">Tipo de trabalho</label>
            </FloatLabel>
          )}
        </div>

        <div className="col-3">
          {isViewMode ? (
            <InputTextComponent
              label="Regime de contratação"
              fieldName="regimeType"
              currentObject={form}
              getValue={getOptionLabel(regimeTypeOptions, form.regimeType)}
              setValue={setForm}
              isDisable
              style={inputStyles}
            />
          ) : (
            <FloatLabel>
              <Dropdown
                inputId="regimeType"
                value={form.regimeType}
                options={regimeTypeOptions}
                optionLabel="label"
                optionValue="value"
                className="w-full"
                filter
                showClear
                onChange={(e) => handleChange("regimeType", e.value)}
              />
              <label className={styles.label} htmlFor="regimeType">Regime</label>
            </FloatLabel>
          )}
        </div>

        <div className="col-3">
          {isViewMode ? (
            <InputTextComponent
              label="Experiência"
              fieldName="experienceLevel"
              currentObject={form}
              getValue={getOptionLabel(experienceLevelOptions, form.experienceLevel)}
              setValue={setForm}
              isDisable
              style={inputStyles}
            />
          ) : (
            <FloatLabel>
              <Dropdown
                inputId="experienceLevel"
                value={form.experienceLevel}
                options={experienceLevelOptions}
                optionLabel="label"
                optionValue="value"
                className="w-full"
                filter
                showClear
                onChange={(e) => handleChange("experienceLevel", e.value)}
              />
              <label className={styles.label} htmlFor="experienceLevel">Experiência</label>
            </FloatLabel>
          )}
        </div>

        <div className="col-3">
          {isViewMode ? (
            <InputTextComponent
              label="Recorrente?"
              fieldName="isRecurring"
              currentObject={form}
              getValue={form.isRecurring ? "Sim" : "Não"}
              setValue={setForm}
              isDisable
              style={inputStyles}
            />
          ) : (
            <FloatLabel>
              <Dropdown
                inputId="isRecurring"
                value={form.isRecurring}
                options={[
                  { label: "Sim", value: true },
                  { label: "Não", value: false },
                ]}
                optionLabel="label"
                optionValue="value"
                className="w-full"
                onChange={(e) => handleChange("isRecurring", e.value)}
              />
              <label className={styles.label} htmlFor="isRecurring">Recorrente? *</label>
            </FloatLabel>
          )}
        </div>
      </div>

      {/* Linha 3 (condicional): Repertir-se a cada (dias) | Próxima execução | Término | Estado | Último email */}

      <div className="grid mb-4" style={{ paddingTop: "12px", rowGap: "12px" }}>
        {form.isRecurring && (
          <div className="col-3">
            <InputNumberComponent
              label="Repertir-se a cada (dias)"
              min={1}
              fieldName="daysRange"
              currentObject={form}
              getValue={form.daysRange}
              setValue={setForm}
              isDisable={isViewMode}
              style={inputStyles}
            />
          </div>
        )}

        {isViewMode && (
          <div className="col-3">
            <InputDateComponent
              label="Próxima execução"
              fieldName="nextExecution"
              currentObject={form}
              getValue={form.nextExecution}
              setValue={setForm}
              isDisable
              style={inputStyles}
            />
          </div>
        )}

        {(form.isRecurring || isViewMode) && (
          <div className="col-3">
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                {isViewMode ? (
                  <InputTextComponent
                    label="Término"
                    fieldName="fineshedExecution"
                    currentObject={form}
                    getValue={
                      !form.isRecurring
                        ? "Não aplicável"
                        : form.fineshedExecution
                          ? formatDateSafe(form.fineshedExecution)
                          : "Indeterminado"
                    }
                    setValue={setForm}
                    isDisable
                    style={inputStyles}
                  />
                ) : (
                  <InputDateComponent
                    label="Término"
                    fieldName="fineshedExecution"
                    currentObject={form}
                    getValue={form.fineshedExecution}
                    setValue={setForm}
                    minDate={minFineshedExecution}
                    showButtonBar
                    style={inputStyles}
                  />
                )}
              </div>
              {form.isRecurring && (
                <>
                  <i
                    className="pi pi-info-circle finished-execution-info"
                    style={{
                      flexShrink: 0,
                      fontSize: "1.1rem",
                      color: "#3b82f6",
                      cursor: "help",
                    }}
                  />
                  <Tooltip
                    target=".finished-execution-info"
                    appendTo={document.body}
                    position="left"
                    style={{ maxWidth: "300px" }}
                  >
                    Para pesquisas com fim indeterminado, deixe este campo vazio.
                  </Tooltip>
                </>
              )}
            </div>
          </div>
        )}

        {mode !== "create" && (
          <div className="col-3">
            <InputTextComponent
              label="Estado"
              fieldName="status"
              currentObject={form}
              getValue={form.status}
              setValue={setForm}
              isDisable
              style={inputStyles}
            />
          </div>
        )}

        {isViewMode && (
          <div className="col-3">
            <InputDateComponent
              label="Último email enviado"
              fieldName="lastEmailedAt"
              currentObject={form}
              getValue={form.lastEmailedAt}
              setValue={setForm}
              isDisable
              style={inputStyles}
            />
          </div>
        )}
      </div>

      {mode !== "view" && (
        <div className="flex justify-content-center align-items-center gap-3">
          <Button className="w-auto" onClick={handleSubmit}>
            {mode === "create" ? "Criar" : "Editar"}
          </Button>
        </div>
      )}
    </div>
  );
};

export default LinkedinRequestsForm;
